import React, { useState, useRef, useEffect } from "react";
import {
  HiOutlineChevronDown,
  HiOutlineXMark,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import "./MultiSelectDropdown.css";

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options",
  label,
  error,
  displayKey = "name",
  valueKey = "id",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null); // ref on the WHOLE component wrapper

  // Close dropdown when clicking outside the entire component
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const toggleOption = (val) => {
    if (disabled) return;
    if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      onChange([...selectedValues, val]);
    }
  };

  const selectAll = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(options.map((o) => o[valueKey]));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const removeSingle = (e, val) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(selectedValues.filter((v) => v !== val));
  };

  const getName = (val) => {
    const opt = options.find((o) => o[valueKey] === val);
    return opt ? opt[displayKey] : val;
  };

  const allSelected =
    options.length > 0 && selectedValues.length === options.length;
  const noneSelected = selectedValues.length === 0;

  /* ---------- display chip(s) inside the control ---------- */
  const renderValue = () => {
    if (noneSelected) {
      return <span className="msd-placeholder">{placeholder}</span>;
    }
    if (selectedValues.length === 1) {
      return (
        <span className="msd-tag">
          {getName(selectedValues[0])}
          {!disabled && (
            <button
              type="button"
              className="msd-tag-x"
              onMouseDown={(e) => removeSingle(e, selectedValues[0])}
            >
              <HiOutlineXMark size={11} />
            </button>
          )}
        </span>
      );
    }
    return (
      <span className="msd-count-badge">{selectedValues.length} selected</span>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={[
        "msd-wrapper",
        isOpen ? "msd-open" : "",
        error ? "msd-error" : "",
        disabled ? "msd-disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && <label className="msd-label">{label}</label>}

      {/* Trigger */}
      <div className="msd-control" onClick={toggleOpen}>
        <div className="msd-value">{renderValue()}</div>
        <div className="msd-right-icons">
          {!noneSelected && !disabled && (
            <button
              type="button"
              className="msd-clear-btn"
              title="Clear all"
              onMouseDown={clearAll}
            >
              <HiOutlineXMark size={14} />
            </button>
          )}
          <HiOutlineChevronDown
            size={16}
            className={`msd-chevron ${isOpen ? "msd-chevron-up" : ""}`}
          />
        </div>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="msd-panel">
          {/* Bulk actions */}
          <div className="msd-bulk-row">
            <button
              type="button"
              className="msd-bulk-btn"
              onMouseDown={selectAll}
              disabled={allSelected}
            >
              <HiOutlineCheckCircle size={12} />
              Select All
            </button>
            <span className="msd-bulk-sep" />
            <button
              type="button"
              className="msd-bulk-btn msd-bulk-clear"
              onMouseDown={clearAll}
              disabled={noneSelected}
            >
              <HiOutlineXMark size={12} />
              Clear All
            </button>
          </div>

          {/* Options list */}
          {options.length === 0 ? (
            <div className="msd-empty">No options available</div>
          ) : (
            options.map((opt) => {
              const val = opt[valueKey];
              const checked = selectedValues.includes(val);
              return (
                <div
                  key={val}
                  className={`msd-option ${checked ? "msd-option-selected" : ""}`}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    toggleOption(val);
                  }}
                >
                  <input
                    type="checkbox"
                    className="msd-checkbox"
                    checked={checked}
                    readOnly
                  />
                  <span className="msd-option-label">{opt[displayKey]}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      {error && <span className="msd-error-msg">{error}</span>}
    </div>
  );
};

export default MultiSelectDropdown;
